import { World, raycast, TAU } from "fast-2d";
import { Camera } from "../render/Camera";
import type { Demo, Scene } from "./types";
import { walls, box, ball, rng } from "./helpers";

/**
 * A "radar" that fans rays out from the pointer and stops each one at the first
 * body it hits — a live showcase of {@link raycast}. Bodies drift and bounce in
 * a sealed box so the silhouette the rays trace is always changing.
 */
export const radar: Demo = {
	id: "radar",
	title: "Raycast radar",
	blurb:
		"A fan of rays sweeps out from your cursor and stops at the first body each one hits — Fast2D's raycast query, visualized. Move the mouse to sweep.",
	category: "Playground & Toys",
	build(): Scene {
		// Zero gravity: the obstacles just drift and bounce around the box.
		const world = new World({ gravity: [0, 0], sleep: false });
		const half = 9;
		const height = 12;
		walls(world, half, height);
		box(world, 0, -height + 0.5, half * 2, 1, { isStatic: true }); // ceiling
		box(world, 0, 0.5, half * 2, 1, { isStatic: true }); // floor

		const random = rng(7);
		// A few static pillars...
		for (let i = 0; i < 5; i++) {
			const x = (random() - 0.5) * 14;
			const y = -2 - random() * 7;
			if (random() < 0.5) box(world, x, y, 0.8 + random(), 0.8 + random(), { isStatic: true });
			else ball(world, x, y, 0.5 + random() * 0.6, { isStatic: true });
		}
		// ...and some drifters bouncing off everything.
		for (let i = 0; i < 7; i++) {
			const x = (random() - 0.5) * 12;
			const y = -3 - random() * 6;
			const v: [number, number] = [(random() - 0.5) * 8, (random() - 0.5) * 8];
			ball(world, x, y, 0.45 + random() * 0.4, { restitution: 1, friction: 0, velocity: v });
		}

		const origin = { x: 0, y: -6 };
		const rays = 90;
		const maxDist = 30;
		const dir: [number, number] = [0, 0];
		const o: [number, number] = [0, 0];

		return {
			world,
			camera: new Camera(0, -6, 30),
			hint: "Move the cursor to sweep the radar",
			draggable: false,
			onPointerMove(x, y): void {
				origin.x = x;
				origin.y = y;
			},
			draw(ctx, cam): void {
				const ox = cam.worldToScreenX(origin.x);
				const oy = cam.worldToScreenY(origin.y);
				o[0] = origin.x;
				o[1] = origin.y;

				for (let i = 0; i < rays; i++) {
					const a = (i / rays) * TAU;
					dir[0] = Math.cos(a);
					dir[1] = Math.sin(a);
					const hit = raycast(world.bodies, o, dir, maxDist);
					const dist = hit ? hit.fraction * maxDist : maxDist;
					const ex = cam.worldToScreenX(origin.x + dir[0] * dist);
					const ey = cam.worldToScreenY(origin.y + dir[1] * dist);

					ctx.strokeStyle = hit ? "rgba(120,220,255,0.35)" : "rgba(120,220,255,0.08)";
					ctx.lineWidth = 1;
					ctx.beginPath();
					ctx.moveTo(ox, oy);
					ctx.lineTo(ex, ey);
					ctx.stroke();

					if (hit) {
						ctx.fillStyle = "rgba(255,220,120,0.95)";
						ctx.beginPath();
						ctx.arc(ex, ey, 2.5, 0, TAU);
						ctx.fill();
					}
				}

				// The emitter itself.
				ctx.fillStyle = "rgba(120,220,255,0.95)";
				ctx.beginPath();
				ctx.arc(ox, oy, 4, 0, TAU);
				ctx.fill();
			},
		};
	},
};
